import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { Subject } from 'rxjs/Subject';
import { Observable, Subscription } from 'rxjs/Rx';
import { FilterService } from '../../services/filter.service';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { Filter, FilterItem } from '../../classes/filter';
import { FilterActionCreatorService } from '../../services/filter-action-creator.service';
import { ManageFilterComponent } from '../manage-filter/manage-filter.component';
import { ConfirmModalComponent } from '@ispm/layout/components/modals/confirm-modal/confirm-modal.component';
import { SelectItem } from 'primeng/api/selectitem';
import { distinctUntilChanged, flatMap, map, take, takeUntil, filter } from 'rxjs/operators';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { FilterUtils } from '../../utils/filter-utils';
import { cloneDeep, get, has, isEqual } from 'lodash';
import { RouterManagerService } from '@ispm/core/services/router-manager.service';
import { CONSTANTS, ConstInterpolatorService, NOTIFICATION_MESSAGE } from '@ispm-client/modules/aggregator/services/consts';
import { SearchService } from '@ispm/dynamic-form/services/search.service';
import { IWorklistColumnItem } from '../../../worklist/interfaces/worklist-action.interface';
import { ITEMS_TYPES } from '@ispm/dynamic-form/constants/ItemsTypes';
import {
  FILTER_DATE_COMING_WEEK,
  FILTER_DATE_LAST_WEEK,
  FILTER_DATE_NEXT_WEEK,
  FILTER_DATE_NOW
} from '../../services/filter.service.const';
import { DynamicFormComponent } from '@ispm/dynamic-form/dynamic-form.component';
import { ReplaceActionService } from '@ispm-client/modules/aggregator/services';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';

@Component({
  selector: 'gen-filter-panel',
  templateUrl: './filter-panel.component.html',
  styleUrls: ['./filter-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class FilterPanelComponent implements OnInit, OnDestroy, AfterViewInit {

  @ViewChildren('filterDynamicForm') dynamicForms: QueryList<DynamicFormComponent>;

  public load: boolean = false;
  public selectedFilter: Filter;
  public selectedFilterCategories: any;
  public filterQuery: string[];
  public filter: Filter;
  public currentFilter$: Observable<Filter>;
  public isFullScreen: boolean = true;
  public payload: any;
  public tunnel$: Subject<any>;
  public callback: (model) => Observable<Response>;
  public type: any;
  public subType: any;
  public enableStatistics: boolean;
  public context: any;
  public filterTypeAndSubtype: any;
  public applyBtn: any = 'Apply';
  public saveBtn: any = 'Save Filter';
  public resourceTitle: string;
  public clearFilterTranslateKey: string;
  public isFilterCreatedOrUpdated: boolean = true;
  public filterItems: SelectItem[] = [];
  public selectedFilterItem: FilterItem;
  public filterSelectedSection: string = 'filter';
  public filterAdminMode: boolean = false;
  public visibleFilterFieldsDetails: IWorklistColumnItem[];
  public inputConfig: {
    [categoryId: string]: {
      formId: string,
      formVertical: boolean,
    }
  } = { };
  public formConfig: {
    [categoryId: string]: {
      filter: any[],
      toggle: boolean,
    }
  } = { };
  public formData: any;
  public isForm = false;

  private filters$: Subscription;
  private selectedFilterCategories$: Subscription;
  private modalSubscriber$: Subscription;
  private asyncdestroyer: Subject<any> = new Subject();
  // Integration with DF
  private prev: any;
  private filterDynamicForms: any[] = [];

  constructor(private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE,
              private authService: AuthenticationService,
              private dynamicModalLoader: DynamicModalLoaderService,
              private filterAC: FilterActionCreatorService,
              private filterService: FilterService,
              private routerManagerService: RouterManagerService,
              private interpolatorService: ConstInterpolatorService,
              private changeDetector: ChangeDetectorRef,
              private _searchService: SearchService,
              private formHandlerService: FormHandlerService) {
    this.currentFilter$ = this.filterAC.getState(['current']).pipe(
      flatMap((current: string) => {
        return this.filterAC.getCurrentAppliedFilters(current);
      }),
      map((currentFilters: any) => {
        if (currentFilters.data) {
          this.load = true;
          this.filter = currentFilters;
          return currentFilters.data;
        }
      })
    );
  }

  ngAfterViewInit() {
    this.dynamicForms.toArray().forEach(form => {
      this.filterDynamicForms.push({
        formId: form.formId,
        hasDataBindings: form.hasDataBindings(),
        dataBindings: cloneDeep(form.getDataBindings()),
        formData: cloneDeep(form.formData),
        initialized: false
      });
    });
  }

  ngOnInit() {
    this.setStateParams();
    this.tunnel$.asObservable().filter(value => value.emitter !== 'child').subscribe(next => {
      next.value && this.submit(this.payload);
    });

    this.type = this.payload.type;
    this.subType = this.payload.subType;
    this.enableStatistics = this.payload.enableStatistics;
    this.context = this.getPayloadValue('context', undefined);
    this.filterTypeAndSubtype = this.payload.type + '_' + this.payload.subType;
    this.applyBtn = this.payload.applyBtnText;
    this.resourceTitle = this.getPayloadValue('resourceTitle', 'Filter');
    this.clearFilterTranslateKey = this.getPayloadValue('clearFilterTranslateKey', 'CLEAR_FILTER_VALUES');
    this.saveBtn = this.payload.saveBtnText;
    this.filterAdminMode = this.payload.filterAdminMode;

    // pass type, subType and annotations (inside additionalParams for aberration type)
    const filterTypeAndSubtype = this.type + '_' + this.subType;
    let filters: Map<string, Filter> = new Map<string, Filter>();
    let defaultFilter;

    this.filters$ = this.filterAC.getCurrentAppliedFiltersContinuously(filterTypeAndSubtype)
      .map(currentFilters => {
        if (currentFilters && currentFilters.data) {
          filters = currentFilters.data;
          this.filterItems = this.getFilterItemsDisplay(filters);
        }
        return filters;
      })
      .flatMap(() => {
        return this.filterService.getDefaultFilter(this.type, this.subType);
      })
      .map(defaultFilterRes => {
        defaultFilter = defaultFilterRes;
      })
      .flatMap(() => {
        return this.filterService.getCurrentDefaultCategories();
      })
      .subscribe();

    this.selectedFilterCategories$ = this.filterService.getCurrentSelectedFilterFromStore()
      .subscribe(selectedFilter => {
        if (selectedFilter) {
          this.filterTypeAndSubtype = selectedFilter.filterTypeAndSubtype;
          const subTypeIdx = selectedFilter.filterTypeAndSubtype.indexOf('_');
          if (subTypeIdx !== -1) {
            this.subType = selectedFilter.filterTypeAndSubtype.slice(subTypeIdx + 1, selectedFilter.filterTypeAndSubtype.length);
          }
          this.selectedFilter = selectedFilter.filter;
          this.selectedFilterCategories = selectedFilter.categories;
          this.selectedFilterItem = this.filterService.setSelectedFilterItem(this.selectedFilter);
          this.changeDetector.markForCheck();
        }
      });
    // TODO Df Integration Goes Here
    this.filterService.getFormFilter().pipe(take(1)).subscribe(data => {
      this.isForm = data.isForm;
      if (this.isForm) {
        this.formData = { };
        this.inputConfig = data.categoryBinding;
        Object.keys(this.inputConfig).forEach(categoryKey => {
          this.filterService.fetchFormInput(this.inputConfig[categoryKey].formId)
            .subscribe(inputForm => (
              this.formConfig[categoryKey] = {
                filter: inputForm.inputs,
                toggle: false,
              })
            );
        });
      }
    });
    this.filterAC.getAppliedFilterProperties().pipe(distinctUntilChanged(), takeUntil(this.asyncdestroyer.asObservable()))
      .subscribe(data => {
        if (data && data.length === 0) {
          this.queryValueChange();
        }
        else {
          this.clearAppliedFilters(data);
        }
      });

    if (!this.filterAdminMode) { //Relevant only for non-adminUI. If it is active in adminUI, save filter will pass old values and laest changes won't be saved
      this.filterService.getCurrentSelectedFilterFromStore(true)
        .pipe(takeUntil(this.asyncdestroyer.asObservable()))
        .filter(data => (data !== undefined))
        .subscribe((data) => {
          this.selectedFilterItem = this.filterService.setSelectedFilterItem(data.filter);
          this.selectedFilterCategories = cloneDeep(data.categories);
          this.formData = (this.isForm && data.filter.formData) ? data.filter.formData : {};
          this.setLastFilterValues();

          if (this.selectedFilterItem['default']) {
            this.applyFilter();
          }
        });
    }
    this.visibleFilterFieldsDetails = this.filterService.currentWorklistColumns;
  }

  setLastFilterValues(){
    //Getting the last filter panel values. In use when user closes the filter panel and shows it again
    if (this.filterService.lastFilterPanelValues && this.filterService.lastFilterPanelValues.selectedFilterId === this.selectedFilter?.id){
      const lastFilterValues = cloneDeep(this.filterService.lastFilterPanelValues);
      this.selectedFilterCategories = lastFilterValues.selectedFilterCategories;
      this.formData = lastFilterValues.formData;
    }
  }

  getPayloadValue(key, defaultValue) {
    if (this.payload.hasOwnProperty(key) && this.payload[key]) {
      return this.payload[key];
    }
    return defaultValue;
  }

  /*
  This function helps us to clear filter form data by listning to applied filter data in store
   */
  public clearAppliedFilters(data: any) {
    const newFormData: any = { };
    if (!!data) {
      // tslint:disable-next-line:forin
      for (const keys in this.formData) {
        const itemData = this.formData[keys];
        newFormData[keys] = { };
        // tslint:disable-next-line:forin
        for (const i in itemData) {
          data.map(item => {
            if (i === item.name) {
              if (item.type.toLowerCase() === 'datepicker') {
                const today = new Date();
                const tempValue = [];
                if (itemData[i] === FILTER_DATE_NEXT_WEEK) {
                  newFormData[keys][i] = [];
                  tempValue.push(new Date(today));
                  tempValue.push(new Date(today.setDate(today.getDate() + 6)));
                  newFormData[keys][i].push(tempValue[0].toPlatformFormat());
                  newFormData[keys][i].push(tempValue[1].toPlatformFormat());
                } else if (itemData[i] === FILTER_DATE_COMING_WEEK) {
                  newFormData[keys][i] = [];
                  // TODO - Previous date selection
                  tempValue.push(new Date(2018, 10, 1));
                  tempValue.push(new Date(today.setDate(today.getDate() + 6)));
                  newFormData[keys][i].push(tempValue[0].toPlatformFormat());
                  newFormData[keys][i].push(tempValue[0].toPlatformFormat());
                } else if (itemData[i] === FILTER_DATE_LAST_WEEK) {
                  newFormData[keys][i] = [];
                  tempValue.push(new Date(today.setDate(today.getDate() - 6)));
                  tempValue.push(today);
                  newFormData[keys][i].push(tempValue[0].toPlatformFormat());
                  newFormData[keys][i].push(tempValue[1].toPlatformFormat());
                } else if (itemData[i] === FILTER_DATE_NOW) {
                  tempValue.push(new Date(today));
                  newFormData[keys][i] = tempValue[0].toPlatformFormat();
                } else {
                  newFormData[keys][i] = itemData[i];
                }
              } else {
                newFormData[keys][i] = itemData[i];
              }
              return;
            }
          });
        }
      }
    }
    this.formData = newFormData;
    this.changeDetector.markForCheck();
  }

  public formChange(form, key) {
    this.prev = this.formData[key];

    if (isEqual(this.prev, form)) { // && !this._valid)
      return;
    }

    this.formData[key] = form;
    this.selectedFilterCategories[key] = this.filterService.createCategories(this.selectedFilterCategories[key].filters, this.formData[key]);
  }// Df Integration Ends Here

  public onChangeSelectedFilter(event: any) {
    if (event && event.value) {
      if (event.value.id === '0') {
        this.selectedFilterItem = this.filterService.setSelectedFilterItem(this.selectedFilter);
        // Check if there are managed filters
        this.filterService.getManagedFiltersFromStore()
          .subscribe((managedFiltersData) => {
            this.openManagedFilters(managedFiltersData, true);
          });
      } else {
        let defaultFilter: Filter;

        // Check if the selected filter in the dropdown is the default filter or a manged filter.
        this.filterService.getDefaultFilter(this.type, this.subType).pipe(
          map(defaultFilterRes => {
            defaultFilter = defaultFilterRes;
          })
        )
          .subscribe();
        if (defaultFilter.id !== event.value.id) {
          // The selected filter in dropdown list is a managed filter.
          this.filterService.getManagedFiltersFromStore()
            .subscribe(managedFilters => {
              const matchingManagedFilters = managedFilters.filter(managedFilter => managedFilter.id === event.value.id);
              if (matchingManagedFilters && matchingManagedFilters.length > 0) {
                this.selectedFilter = matchingManagedFilters[0];
              }
            });
        } else {
          this.selectedFilter = defaultFilter;
        }
        // TODO check if Form Filter get data from saved Filter
        // if yes Then Replace formData with FormDAta from selected Filter and exit
        if (this.isForm) {
          this.queryValueChange(this.selectedFilter.formData);
        }
        this.filterAC.getState(['current'])
          .take(1)
          .subscribe((currentFilterTypeAndSubtype: string) => {
            this.filterService.initSelectedFilterPropsWithSelectedFilterValues(
              currentFilterTypeAndSubtype, this.selectedFilter, this.changeDetector);
            this.applyFilter(currentFilterTypeAndSubtype);
            this.isFilterCreatedOrUpdated = true;
            this.changeDetector.markForCheck();
          });
      }
    }
  }

  submit(model) {
    this.callback(model)
      .subscribe((model) => {
        this.tunnel$.next({ emitter: 'child', value: model});
      });
  }

  public saveFilter(): void {
    let orderedManagedFilters: any[] = [];
    this.filterService.getManagedFiltersFromStore()
      .subscribe(managedFilters => {
        orderedManagedFilters = managedFilters;
        // if(this.isForm) {
        //   managedFilters.forEach((filters, i) => {
        //     orderedManagedFilters[i].formData = this.formData;
        //   })
        // }
      });

    this.filterService.getDefaultFilter(this.type, this.subType)
      .subscribe(defaultFilter => {
        if (defaultFilter) {

          if (this.selectedFilterItem.id !== defaultFilter.id) {
            this.openCreateFilterModal(orderedManagedFilters, this.selectedFilterItem.label);
          } else {
            // Trying to save default filter. Pass the list of managed filters.
            this.openCreateFilterModal(orderedManagedFilters);
          }
        }
      });
  }

  public deleteOrUpdateFromFilterItems(filter: any): void {
    if (filter instanceof Object) {
      // Perform update
      this.updateFilterInFilterItems(filter);
    } else {
      // PerformDelete.
      this.deleteFromFilterItems(filter);
    }
  }

  public applyFilter(currentFilterTypeAndSubtype?: string): void {
    /** Get the filter properties from the categories */
    const filterProperties: any[] = [];
    const categoryKeys: string[] = Object.keys(this.selectedFilterCategories);
    if (categoryKeys) {
      categoryKeys.forEach(key => {
        this.isForm ? filterProperties.push(...this.removeDefaultDateValue(this.selectedFilterCategories[key].filters))
          : filterProperties.push(...this.selectedFilterCategories[key].filters);
      });
      this.filterService.setAppliedFilterProperties(filterProperties, currentFilterTypeAndSubtype);
    }

    // region filter disabled checkboxes
    Object.keys(this.selectedFilterCategories).forEach(selectedCategoryKey => {
      this.selectedFilterCategories[selectedCategoryKey].filters.forEach(categoryFilter => {
        // if checkbox has value array which consists of one object with empty array in 'id'
        if (categoryFilter.type === ITEMS_TYPES.CHECKBOX &&
          Array.isArray(categoryFilter.selectedValue.value) &&
          categoryFilter.selectedValue.value.length === 1 &&
          !categoryFilter.selectedValue.value[0].id.length
        ) {
          categoryFilter.selectedValue.value = null;
        }
      });
    });

    const cleanFormData = Object.keys(this.formData)
      .filter(itemKey => this.formData[itemKey].length)
      .reduce((accum, itemKey) => {
        accum[itemKey] = this.formData[itemKey];
        return accum;
      }, { });
    // endregion filter disabled checkboxes

    this.filterService.applyFilter(this.type, this.subType, this.selectedFilterCategories, cleanFormData);
    this.isFilterCreatedOrUpdated = true;
  }

  public clearFilterProperties(): void {
    this.filterService.clearFilterPropertiesInStore()
      .subscribe((currentSelectedFilter) => {
        if (currentSelectedFilter) {
          if (this.isForm) {
            this.queryValueChange(undefined, true);
          }
          this.selectedFilterCategories = cloneDeep(currentSelectedFilter.categories);
        }
      });
  }

  ngOnDestroy(): void {
    this.filterService.lastFilterPanelValues = {selectedFilterId: this.selectedFilter?.id,
      selectedFilterCategories: cloneDeep(this.selectedFilterCategories), formData: cloneDeep(this.formData)};
    if (this.filters$) {
      this.filters$.unsubscribe();
    }
    if (this.modalSubscriber$) {
      this.modalSubscriber$.unsubscribe();
    }
    if (this.selectedFilterCategories$) {
      this.selectedFilterCategories$.unsubscribe();
    }
    this.asyncdestroyer.next();
    this.asyncdestroyer.complete();

  }

  setStateParams() {
    const stateParams = this.routerManagerService.parameters;
    if (stateParams && stateParams.hasOwnProperty('isFullScreen')) {
      this.isFullScreen = stateParams.isFullScreen;
    }
  }

  // Df Integration starts here
  private queryValueChange(data?, resetToInit: boolean = false) {
    if (!data) {
      data = {};
      for (const keys in this.inputConfig) {
        data[keys] = {};
      }
    }

    for (const keys in this.inputConfig) {
      this.formData[keys] = this.updateSelectedFormFilterValue(this.formConfig[keys].filter, data[keys], resetToInit);
      this.filterService.getDefaultFilter(this.type, this.subType)
        .subscribe(defaultFilter => {
          if (defaultFilter) {
            this.selectedFilterItem = defaultFilter;
          }
        });
      if (!resetToInit) {
        this.filterDynamicForms.forEach(item => {
          if (item.formId === get(this.inputConfig[keys], 'formId') && item.hasDataBindings) {
            this.formData[keys] = item.formData;
          }
        });
      }
      else {
        this.filterDynamicForms.forEach(item => {
          if (item.formId === get(this.inputConfig[keys], 'formId') && item.hasDataBindings) {
            item.formData = this.formData[keys];
          }
        });
      }
    }
    this.changeDetector.markForCheck();
  }

  private openCreateFilterModal(orderedMnagedFilters: any[] = [], selectedFilterName: string = ''): void {
    const payload = Object.assign(new PlaceholderModalLoaderPayload(
      '',
      ManageFilterComponent,
      'Save',
      'Cancel',
      {
        filters: orderedMnagedFilters,
        isManageDialog: false,
        isManageTitle: false,
        selectedFilter: selectedFilterName,
        resourceTitle: this.payload.resourceTitle
      },
      (newFilter, from: boolean = false) => {

        let filterName = newFilter;
        if (typeof newFilter === 'string') {
          filterName = newFilter;
        } else if (newFilter.name) { // in some cases, 'newFilter' might be the whole filter object - see: GEN-87428
          filterName = newFilter.name;
        }

        this.createOrUpdateFilter(filterName, from);
      }
      ),
      {
        buttonsStatuses: {
          approveBtn: true,
          declineBtn: false
        }
      });
    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent, payload)
      .subscribe(() => {
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      });
  }

  private openManagedFilters(managedFilters: Filter[], isManageTitle: boolean = false): void {
    let payload: any;
    const innerPayload: any = { };

    innerPayload.filters = [];
    innerPayload.isManageDialog = true;
    innerPayload.isManageTitle = isManageTitle;
    innerPayload.resourceTitle = this.resourceTitle;

    if (managedFilters.length > 0) {
      innerPayload.filters = managedFilters;
      payload = Object.assign(new PlaceholderModalLoaderPayload(
        'Manage ' + this.resourceTitle,
        ManageFilterComponent,
        'Save',
        'Cancel',
        innerPayload,
        (filter) => {
          this.deleteOrUpdateFromFilterItems(filter);
        }
        ),
        {
          buttonsStatuses: {
            declineBtn: false
          }
        });
    } else {
      payload = Object.assign(new PlaceholderModalLoaderPayload(
        'Manage ' + this.resourceTitle,
        ManageFilterComponent,
        '',
        'Cancel',
        innerPayload
        ),
        {
          buttonsStatuses: {
            declineBtn: false
          }
        });
    }

    this.modalSubscriber$ = this.dynamicModalLoader.popupModal(PlaceholderModalComponent, payload)
      .subscribe(() => {
        this.modalSubscriber$.unsubscribe();
        this.modalSubscriber$ = null;
      });
  }

  private createOrUpdateFilter(newFilterName: string, fromDeletePanel: boolean = false): void {
    if (this.filterService.isDefaultFilter(newFilterName)) {
      // Set filter properties to default filter
      this.filterAC.getState(['current'])
        .take(1)
        .subscribe((current: string) => {
          this.filterService.initSelectedFilterPropsWithSelectedFilterValues(current, this.selectedFilter, this.changeDetector);
        });
      this.notificationMessageService.error(this.interpolatorService.interpolate(CONSTANTS.DEFAULT_FILTER_CANNOT_BE_CHANGED, this.resourceTitle.toLowerCase()));

    } else {
      if (this.isForm) {
        for (const keys in this.formConfig) {
          const formDef = this.formConfig[keys].filter;
          for (const formName in formDef) {
            if (formDef[formName].noSaveData && formDef[formName].affectedField) {
              const affectedFiled = formDef[formName].affectedField;
              this.selectedFilterCategories[keys].filters.map(categoryData => {
                if (categoryData.name === affectedFiled) {
                  categoryData.selectedValue.value = [];
                  if (this.formData[keys][affectedFiled]) {
                    const affectedData = this.formData[keys][affectedFiled];
                    if (Array.isArray(affectedData)) {
                      affectedData.forEach(data => {
                        categoryData.selectedValue.value.push({ id: data});
                      });
                    }
                    else {
                      categoryData.selectedValue.value.push({ id: affectedData});
                    }
                  }
                }
                return categoryData;
              });
            }
          }
        }
      }

      const filterQuery = this.filterService.buildFilterQuery(this.selectedFilterCategories);
      const existsFilter: Filter = this.filterService.filterNameExists(newFilterName);

      if (existsFilter && Object.keys(existsFilter).length > 0) {
        if (existsFilter.hasOwnProperty('undeletable') && existsFilter.undeletable) {
          this.notificationMessageService.error(this.interpolatorService.interpolate(CONSTANTS.DEFAULT_FILTER_CANNOT_BE_CHANGED, this.resourceTitle.toLowerCase()));
        } else {
          if (this.isForm) {
            existsFilter.formData = this.formData;
          }
          existsFilter.query = filterQuery;
          this.updateManagedFilter(existsFilter);
        }
      } else {
        if (this.selectedFilter) {
          const context: any = { };

          if (this.selectedFilter.filterDefinitionId) {
            context.filterDefinitionId = this.selectedFilter.filterDefinitionId;
          }
          else {
            if (this.context) { // testTypeId
              context.testTypeId = this.context;
            }
          }

          if (!fromDeletePanel) {
            this.createNewFilter(newFilterName, filterQuery, this.selectedFilterCategories, context);
          }
        }
      }
    }
  }

  private updateManagedFilter(filterToUpdate: Filter) {
    this.dynamicModalLoader.popupModal(ConfirmModalComponent,
      new ConfirmModalLoaderPayload('Update ' + this.resourceTitle, `Do you want to overwrite ${filterToUpdate.name} ${this.resourceTitle.toLowerCase()}?`, 'Confirm', 'Cancel'),
      ModalTypes.ALERT
    ).pipe(
      map(res => res.payload),
      flatMap((res: modalResponse) => {
        switch (res) {
          case modalResponse.approve:
            return this.filterService.updateManagedFilter(filterToUpdate).take(1);
          default:
            return Observable.empty();
        }
      })
    )
      .subscribe(
        (res) => {
          this.filterAC.setAppliedFilter(res[0]);
          this.notificationMessageService.success(this.resourceTitle + ' ' + this.notificationMessage.UPDATED_SUCCESSFULLY);
          this.selectedFilter = filterToUpdate;

          if (!!this.selectedFilter) {
            this.selectedFilterItem = this.filterService.setSelectedFilterItem(this.selectedFilter);
          }

          this.applyFilter();
          this.isFilterCreatedOrUpdated = true;
        },
        () => {
          this.notificationMessageService.error(
            this.notificationMessage.FAILED_TO_UPDATE + ' ' + this.resourceTitle.toLowerCase() + ': ' + filterToUpdate.name
          );
        },
        () => this.changeDetector.markForCheck()
      );
  }

  private createNewFilter(newFilterName: string, filterQuery: string, categories: any, context?: any): void {
    let formData;

    if (this.isForm) {
      formData = this.formData;
    }

    this.filterService.createFilter(newFilterName, this.type, this.subType, filterQuery, context, false, formData)
      .pipe(
        filter(data => !!data)
      )
      .subscribe(
        (res) => {
          const newFilter: Filter = <Filter> res;
          this.filterAC.setAppliedFilter(newFilter, categories);
          this.selectedFilter = newFilter;
          this.selectedFilterItem = this.filterService.setSelectedFilterItem(this.selectedFilter);
          this.selectedFilterCategories = categories;
          this.notificationMessageService.success(this.resourceTitle + ' ' + this.notificationMessage.CREATED_SUCCESSFULLY);
          this.applyFilter();
          this.isFilterCreatedOrUpdated = true;
        },
        (err) => {
          this.notificationMessageService.error(
            this.notificationMessage.FAILURE_TO_CREATE + this.resourceTitle.toLowerCase() + ' :' + err.message
          );
        },
        () => this.changeDetector.markForCheck()
      );
  }

  private updateFilterInFilterItems(filter: Filter): void {
    const selectItemIndexToUpdate: number = this.filterItems.findIndex((selectItem: SelectItem) => selectItem.value.id === filter.id);
    if (selectItemIndexToUpdate !== -1) {
      const filterItem = {
        label: filter.name,
        id: filter.id,
        undeletable: filter.hasOwnProperty('undeletable') ? filter.undeletable : false,
        disabled: filter.id === '0'
      };
      this.filterItems[selectItemIndexToUpdate] = { label: filter.name, value: filterItem};
      this.changeDetector.markForCheck();
    }
  }

  private deleteFromFilterItems(filterId: string): void {
    this.filterItems = this.filterItems
      .filter((selectItem: SelectItem) => selectItem.value.id !== filterId);
    /** Set the default filter as the selected filter and update the current
     selected query to the query of the default filter and matching catgories.
     */
    this.filterService.getDefaultFilter(this.type, this.subType).pipe(
      map(defaultFilter => {
        this.selectedFilter = defaultFilter;
      }),
      flatMap(() => {
        return this.filterService.getCurrentDefaultCategories();
      })
    )
      .subscribe(defaultCategories => {
        let queryObj;

        if (!this.selectedFilter.hasOwnProperty('queryMandatory')) {
          queryObj = this.filterService.buildInitialFilterQuery(this.selectedFilter.query);
        } else {
          queryObj = this.filterService.buildInitialFilterQuery(this.selectedFilter.query, this.selectedFilter.queryMandatory);
        }

        if (this.isForm) {
          this.queryValueChange(this.selectedFilter.formData);
        }

        const filterTypeAndSubtype = FilterUtils.buildFilterTypes(this.type, [this.subType]);
        const currentSelectedFilterAndCategories = {
          filter: this.selectedFilter,
          categories: defaultCategories,
          filterTypeAndSubtype: filterTypeAndSubtype
        };
        this.filterAC.setCurrentSelectedFilter(currentSelectedFilterAndCategories);
        this.filterAC.setCurrentSelectedQuery(this.type, this.subType, queryObj, this.formData);
        this.selectedFilterCategories = defaultCategories;
        this.applyFilter(filterTypeAndSubtype);
        this.changeDetector.markForCheck();
      });
  }

  private getFilterItemsDisplay(currentFilters: Map<string, Filter>): SelectItem[] {
    const filterItems: SelectItem[] = [];
    const defaultFilters: Filter[] = [];
    const managedFilters: Filter[] = [];
    let managedFiltersGroup: Filter;
    const filtersData: any[] = [];
    const currentFiltersData = Array.from(currentFilters.values());
    if (currentFiltersData) {
      currentFiltersData.forEach((currentFilterData: Filter) => {
        if (!currentFilterData.default) {
          if (currentFilterData.id !== '0') {
            managedFilters.push(currentFilterData);
          }
          else {
            managedFiltersGroup = currentFilterData;
          }
        } else {
          defaultFilters.push(currentFilterData);
        }
      });
    }

    const orderedManagedFilters: Filter[] = this.filterService.orderManagedFiltersAscByCreateData(managedFilters);

    if (defaultFilters.length > 0) {
      filtersData.push(...defaultFilters);
    }
    if (orderedManagedFilters.length > 0 && !this.filterAdminMode) {
      filtersData.push(...orderedManagedFilters);
    }
    if (managedFiltersGroup && !this.filterAdminMode) {
      filtersData.push(managedFiltersGroup);
    }

    filtersData.forEach((filter: Filter) => {
      const filterItem = {
        label: filter.name,
        id: filter.id,
        undeletable: filter.hasOwnProperty('undeletable') ? filter.undeletable : false,
        disabled: filter.id === '0'
      };
      filterItems.push({ label: filter.name, value: filterItem});
    });

    return filterItems;
  }

  private updateSelectedFormFilterValue(form: any, newValue: any, resetToInit: boolean = false) {
    const tempFormData = { };
    form.map(filter => {
      if(resetToInit){
        tempFormData[filter.name] = get(filter,'defaultValue');
        return;
      }
      else if (!newValue[filter.name]) {
        return;
      }
      const transformTemplate = this._searchService.getTransformObject(get(filter, 'options.resource'));
      switch (filter.type.toLocaleUpperCase()) {
        case 'DROPDOWN':
        case 'SELECT':
        case 'AUTOCOMPLETE':
          if (typeof newValue[filter.name] === 'string') {
            tempFormData[filter.name] = newValue[filter.name];
          }
          else {
            tempFormData[filter.name] = {
              code: newValue[filter.name] ? newValue[filter.name].code : null,
              display: newValue[filter.name] ? newValue[filter.name].display : null
            };
          }
          break;
        case 'MULTISELECT_WITH_MANUAL':
        case 'MULTISELECT':
          tempFormData[filter.name] = [];
          if (newValue[filter.name]) {
            if (typeof(newValue[filter.name]) === 'string') {
              newValue[filter.name] = newValue[filter.name].split(',');
            }
            newValue[filter.name].map(data => {
              const obj = { };
              const code = get(transformTemplate, 'code', 'code');
              const display = get(transformTemplate, 'display', 'display');
              obj[`${code}`] = get(data, `${code}`);
              obj[`${display}`] = get(data, `${display}`);
              tempFormData[filter.name].push(obj);
            });
          }
          break;
        default:
          tempFormData[filter.name] = newValue[filter.name];
          break;
      }

    });
    return tempFormData;
  }

  private removeDefaultDateValue(categories) {
    categories.map((formItem, index) => {
      if (formItem.type.toUpperCase() === ITEMS_TYPES.DATEPICKER && formItem.selectedValue.value && Array.isArray(formItem.selectedValue.value) && formItem.selectedValue.value.length > 0) {
        const today = new Date();
        const tempValue = [];
        const selectedValue = formItem.selectedValue.value[0].id;
        if (typeof selectedValue === 'string') {
          if (selectedValue.toLowerCase() === FILTER_DATE_NEXT_WEEK) {
            categories[index].selectedValue.value = [];
            tempValue.push(new Date(today));
            tempValue.push(new Date(today.setDate(today.getDate() + 6)));
            categories[index].selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});
            categories[index].selectedValue.value.push({ id: tempValue[1].toPlatformFormat()});
          }
          if (selectedValue.toLowerCase() === FILTER_DATE_COMING_WEEK) {
            categories[index].selectedValue.value = [];
            // TODO - Previous date selection
            tempValue.push(new Date(2018, 10, 1));
            tempValue.push(new Date(today.setDate(today.getDate() + 6)));
            categories[index].selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});
            categories[index].selectedValue.value.push({ id: tempValue[1].toPlatformFormat()});
          }
          if (selectedValue.toLowerCase() === FILTER_DATE_NOW) {
            categories[index].selectedValue.value = [];
            tempValue.push(new Date(today));
            categories[index].selectedValue.value.push({ id: tempValue[0].toPlatformFormat()});
          }
        }
      }
    });
    return categories;
  }

  // executes only on form initialization
  public onFormInit($event) {
    this.filterDynamicForms.forEach(item => {
      if (item.formId === $event.formId && !item.initialized && !!$event.formData && item.hasDataBindings) {
        item.formData = cloneDeep($event.formData);

        // update in formHandler this form with default data bindings
        Object.keys(this.inputConfig)
          .forEach(key => {
            if (this.inputConfig[key].formId === item.formId) {
              const formInputs = this.formConfig[key].filter;
              formInputs.forEach(input => ReplaceActionService.replace(item.dataBindings, item.formData, input));
              this.formHandlerService.updateForm(this.inputConfig[key].formId, formInputs);
            }
          });
        item.initialized = true;
      }
    });
  }

}
